import assign from "lodash/assign";
import { useFormik } from "formik";
import {
  useDebounce,
  useEffectOnce,
  useMountedState,
  useUpdateEffect
} from "react-use";
import { useCallback, useState, useRef } from "react";
import pick from "lodash/pick";
import { isCompletelyEmpty } from "core/utils/isCompletelyEmpty";
import { useEffect } from "react";

/**
 * React hook which helps with automatic validation of state variables which are being used in form inputs.
 * @param model {object} The object which fields being validated
 * @param onFormValidated {function} The callback which will receive the validated model object.
 * @param validationSchema {object} Yup validation schema
 * @param checkInitTracker {boolean}
 * @returns {object}
 */
export function useFormAutoValidation(
  model,
  onFormValidated,
  validationSchema = null,
  checkInitTracker = true
) {
  const isMounted = useMountedState();
  const filteredModel = validationSchema
    ? pick(model, Object.keys(validationSchema.fields))
    : model;
  const [initTracker, setInitTracker] = useState(0);

  const formik = useFormik({
    initialValues: filteredModel,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      if (!isMounted) {
        return;
      }

      // We clone the model so the reference becomes different which will trigger a rerender
      const m = assign({ ...model }, values);
      const equals = JSON.stringify(m) === JSON.stringify(model);

      if (onFormValidated && !equals) {
        onFormValidated({ model: m, isValid: true });
      }
      setSubmitting(false);
    }
  });

  useDebounce(
    () => {
      if (!formik.isValidating && !formik.isSubmitting) {
        formik.submitForm().then(() => {
          if (!isMounted) {
            return;
          }
          if (formik.isValid) {
            formik.setErrors([]);
          }
        });
      }
    },
    500,
    [formik.values]
  );

  const prevId = useRef(filteredModel?.id);

  useEffect(() => {
    // Only reset form if the case id changes
    if (filteredModel?.id !== prevId.current) {
      if (!isCompletelyEmpty(filteredModel)) {
        formik.setValues(filteredModel);
      }
      prevId.current = filteredModel?.id;
    }
    // eslint-disable-next-line
  }, [filteredModel?.id]);

  useUpdateEffect(() => {
    if (initTracker === 0) {
      return;
    }
    onFormValidated({
      model: assign({ ...model }, formik?.values),
      isValid: !!formik?.isValid
    });
  }, [formik?.isValid]);

  useEffectOnce(() => {
    if (!validationSchema?.fields) {
      return;
    }
    // disable initTracker
    if (!checkInitTracker) {
      setInitTracker(x => x + 1);
    }

    // touch fields after first render so errors will appear.
    const t = setTimeout(() => {
      if (!isMounted) {
        return;
      }
      const fields = {};
      Object.keys(validationSchema.fields).forEach(x => {
        fields[x] = true;
      });
      formik.setTouched(fields);
    }, 500);
    return () => {
      clearTimeout(t);
    };
  });

  return formik;
}

/**
 * React hook which helps with on-demand validation of state variables which are being used in form inputs.
 * @param model {object} The object which fields being validated
 * @param onFormValidated {function} The callback which will receive the validated model object.
 * @param validationSchema {object} Yup validation schema
 * @returns {array}
 */
export function useFormValidation(
  model,
  onFormValidated,
  validationSchema = null
) {
  const isMounted = useMountedState();
  const formik = useFormik({
    initialValues: model,
    validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      return new Promise(resolve => {
        setTimeout(() => {
          if (!isMounted) {
            return;
          }
          const m = assign({ ...model }, values);
          const equals = JSON.stringify(m) === JSON.stringify(model);
          let v;
          if (onFormValidated && !equals) {
            v = onFormValidated(m);
          }
          setSubmitting(false);
          resolve(v);
        }, 1);
      });
    }
  });

  const validateForm = useCallback(() => {
    if (!formik.isValidating && !formik.isSubmitting) {
      return formik.submitForm();
    }
    // eslint-disable-next-line
  }, [formik]);

  return [formik, validateForm];
}
